import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { request } from '../services/client'

// ── Static fallback content (shown while API loads or if it fails) ──────────
const FALLBACK = {
  terms: {
    title: 'Terms of Service',
    updatedAt: null,
    body: `Article 1: Purpose
This Terms of Service aims to regulate the rights, obligations, and responsibilities between the Company and users regarding the use of the online community service provided by "Chicago Community" (hereinafter referred to as the "Service").

Article 2: Effectiveness and Amendment of Terms
These Terms shall take effect by being posted on the Service screen or notified through other methods. The Company may amend these Terms to the extent that it does not violate relevant laws and regulations, and will provide prior notice upon amendment. Users who do not agree to the amended Terms may discontinue use of the Service and withdraw their membership.

Article 3: Definitions
"User" refers to a member who uses the Service in accordance with these Terms. "Member" refers to a person who has registered with the Service, received a user account, and uses the Service. "Post" means text, comments, images, links, etc., posted by users on the Service.

Article 4: Provision of Service
The Company provides the following services: Community services such as free bulletin boards, information boards, politics, humor/inspiration, chart analysis, etc.; Operation of Best Posts board; Operation of paid boards; Advertising posting function; Other services determined by the Company. The Company may temporarily suspend the provision of services for system maintenance, technical issues, or operational needs.

Article 5: Membership Registration
Users can register as members by completing the Company's registration process, including email verification. The Company may refuse membership registration in the following cases: Impersonation using another person's information; Providing false information; Cases deemed to intend to interfere with the operation of the Service.

Article 6: Member Obligations
Members shall not engage in the following acts: Impersonation using another person's information; Posting illegal information, obscene, or violent content; Commercial advertising, promotion, or spam posting; Acts that interfere with the operation of the Service; Acts that violate laws or these Terms. In case of violation, the Company may take measures such as deletion of posts, restriction of use, or account deletion.

Article 7: Post Management
Members bear full responsibility for the posts they create. The Company may delete the following posts without prior notice: Profanity, slander, or defamation; Obscene or illegal content; Dissemination of false information; Political incitement or promotion of social conflict; Advertising or spam; Other violations of operational policies. The Company may provide information to relevant authorities upon request regarding posts that violate laws.

Article 8: Copyright
Copyright of posts created by members belongs to the members. Members grant the Company the right to use, store, modify, and repost their posts for the purpose of operating and promoting the Service.

Article 9: Restriction on Service Use
The Company may restrict service use without prior notice in the following cases: Violation of Terms; Violation of operational policies; Illegal activities; System attacks or causing disruptions. In cases of serious violations, the Company may permanently suspend the account.

Article 10: Limitation of Liability
The Company shall not be liable for: Disputes between users; The reliability of information posted by users; Damages caused by service interruptions or failures. The Company shall not be liable for damages related to the use of free services.

Article 11: Miscellaneous
Matters not stipulated in these Terms shall be governed by relevant laws and commercial practices.`,
  },
  privacy: {
    title: 'Privacy Policy',
    updatedAt: null,
    body: `Chicago Community (hereinafter referred to as the "Company") establishes and operates the following Privacy Policy to safely protect users' personal information in accordance with the Personal Information Protection Act and other relevant laws.

Article 1: Personal Information Collected
Upon membership registration: Email, password, nickname, IP address, device information. Automatically collected during service use: Access logs, IP address, browser information, usage records.

Article 2: Purposes of Collection and Use of Personal Information
Member identification and login authentication; Service provision and community operation; Prevention of fraudulent use and security enhancement; Operation of advertisements and events, and statistical analysis; Compliance with laws and dispute resolution.

Article 3: Retention and Use Period of Personal Information
Personal information is immediately deleted upon membership withdrawal. However, if required by law to be retained, it will be kept for the specified period. Log records: 3 months (Protection of Communications Secrets Act). Prevention of fraudulent use: 1 year.

Article 4: Provision of Personal Information to Third Parties
The Company does not provide personal information to third parties without the user's consent. Exceptions apply in cases of requests pursuant to laws and regulations.

Article 5: Entrustment of Personal Information Processing
The Company does not entrust the processing of personal information to external parties. (Any future changes will be announced separately.)

Article 6: User Rights
Users may request access to, correction of, or deletion of their personal information. Users may request deletion of personal information at any time through membership withdrawal.

Article 7: Operation of Cookies
The Company may use cookies to provide customized services. Users may refuse cookie storage through browser settings.

Article 8: Security Measures for Personal Information
The Company implements the following security measures: Encrypted storage of passwords; Access authority control; Server and network security enhancement.

Article 9: Personal Information Protection Officer
The Company designates a person responsible for personal information protection. Email: chicagocoinproject@chicagocoin.io

Article 10: Notification of Changes
In the event of any changes to this policy, the Company will notify users through announcements within the service.`,
  },
  youth: {
    title: 'Youth Protection Policy',
    updatedAt: null,
    body: `Chicago Community (hereinafter referred to as the "Company") establishes and operates a Youth Protection Policy to protect adolescents from exposure to harmful information.

Article 1: Purpose
This policy aims to establish measures to block harmful information and provide protection so that adolescents can use the service safely.

Article 2: Blocking Harmful Information
The Company prohibits the following harmful content: Obscene or sexual content; Content that promotes violence, suicide, or self-harm; Information inducing drugs, gambling, or illegal activities; Profanity, hate speech, or discriminatory statements. Violating content will be immediately deleted, and if necessary, reported to the relevant authorities.

Article 3: Measures for Youth Protection
Boards or content inaccessible to adolescents under 19 years of age are restricted through an adult verification process. The Company strengthens monitoring of harmful content and makes every effort to protect adolescents.

Article 4: User Reporting System
Users may report harmful content upon discovery, and the Company will promptly process such reports.

Article 5: Designation of Responsible Person
The Company designates the youth protection officer as follows: Youth Protection Officer: Operations Team. Email: Chicagocoinproject@chicagocoin.io. In the event of any changes to this policy, the Company will notify users through service announcements.`,
  },
}

const TABS = [
  { id: 'terms',   label: 'Terms of Service',      path: '/terms',   icon: 'gavel' },
  { id: 'privacy', label: 'Privacy Policy',         path: '/privacy', icon: 'privacy_tip' },
  { id: 'youth',   label: 'Youth Protection',       path: '/youth-protection', icon: 'child_care' },
]

function pathToTab(pathname) {
  if (pathname === '/privacy')          return 'privacy'
  if (pathname === '/youth-protection') return 'youth'
  return 'terms'
}

export default function LegalPage() {
  const location = useLocation()
  const navigate  = useNavigate()
  const activeId  = pathToTab(location.pathname)

  // Try to fetch live policy content from the backend
  const [policies, setPolicies] = useState(null)
  const [loadingPolicy, setLoadingPolicy] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoadingPolicy(true)
    request('/settings/policies')
      .then(json => {
        if (!cancelled && json?.data) setPolicies(json.data)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingPolicy(false) })
    return () => { cancelled = true }
  }, [])

  const content = policies?.[activeId] ?? FALLBACK[activeId]

  // Split body into paragraphs on double-newline or on "Article N:"
  const paragraphs = content.body
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(Boolean)

  return (
    <div className="min-h-screen bg-surface-bright">
      {/* ── Top bar ── */}
      <div className="bg-on-background px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <Link to="/login" className="font-extrabold text-[20px] uppercase tracking-tight text-surface">
          Chicago
        </Link>
        <Link
          to="/login"
          className="flex items-center gap-2 font-bold text-[11px] uppercase tracking-widest text-surface/70 hover:text-surface transition-colors"
        >
          <span className="material-symbols-outlined text-[16px]">arrow_back</span>
          Back
        </Link>
      </div>

      <div className="max-w-[860px] mx-auto px-4 py-10">
        {/* ── Page title ── */}
        <div className="mb-8">
          <h1 className="font-extrabold text-[36px] uppercase tracking-tight leading-none text-on-surface">
            Legal
          </h1>
          <p className="text-sm text-on-surface-variant font-medium mt-2">
            Chicago Community — policies and terms that govern your use of the service.
          </p>
        </div>

        {/* ── Tab nav ── */}
        <div className="flex gap-0 border-4 border-on-background mb-8 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex-1 min-w-[130px] flex items-center justify-center gap-2 py-3 px-4 font-bold text-[11px] uppercase tracking-widest transition-all border-r-4 border-on-background last:border-r-0
                ${activeId === tab.id
                  ? 'bg-on-background text-surface'
                  : 'bg-surface text-on-surface hover:bg-surface-container'}`}
            >
              <span className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                {tab.icon}
              </span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Content card ── */}
        <div
          className="bg-surface border-4 border-on-background"
          style={{ boxShadow: '8px 8px 0px 0px var(--neo-border-color)' }}
        >
          {/* Card header */}
          <div className="border-b-4 border-on-background px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                className="material-symbols-outlined text-[22px]"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                {TABS.find(t => t.id === activeId)?.icon}
              </span>
              <h2 className="font-extrabold text-[18px] uppercase tracking-tight">
                {content.title}
              </h2>
            </div>
            {content.updatedAt && (
              <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
                Updated {new Date(content.updatedAt).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Body */}
          <div className="px-8 py-8">
            {loadingPolicy && !policies ? (
              <div className="flex items-center gap-3 text-on-surface-variant py-4">
                <div className="w-5 h-5 border-[3px] border-current border-t-transparent animate-spin" />
                <span className="font-bold text-sm uppercase tracking-wider">Loading…</span>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {paragraphs.map((para, i) => {
                  const isArticleHeader = /^Article\s+\d+:/i.test(para)
                  return isArticleHeader ? (
                    <h3
                      key={i}
                      className="font-extrabold text-[14px] uppercase tracking-wider text-on-surface mt-4 first:mt-0 border-l-4 border-primary-container pl-3"
                    >
                      {para}
                    </h3>
                  ) : (
                    <p key={i} className="text-[14px] text-on-surface-variant leading-relaxed font-medium">
                      {para}
                    </p>
                  )
                })}
              </div>
            )}
          </div>

          {/* Card footer */}
          <div className="border-t-4 border-on-background px-8 py-4 bg-surface-container-low flex items-center justify-between">
            <span className="font-bold text-[9px] uppercase tracking-[0.18em] text-on-surface-variant">
              Chicago Community · chicagonews.io
            </span>
            <a
              href="mailto:chicagocoinproject@chicagocoin.io"
              className="font-bold text-[9px] uppercase tracking-[0.12em] text-on-surface-variant hover:text-primary transition-colors"
            >
              chicagocoinproject@chicagocoin.io
            </a>
          </div>
        </div>

        {/* ── Tab nav footer ── */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          {TABS.filter(t => t.id !== activeId).map(tab => (
            <Link
              key={tab.id}
              to={tab.path}
              className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary"
            >
              {tab.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
